import { query } from 'operations';
import { useParams } from 'react-router-dom';
import { DialogTypes, GeneralIds, ModelTypes } from 'types/types';
import { FormTemplateType, Visit } from 'api/models';
import { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { PatientCard } from './PatientCard';
import VisitsTable from './VisitsTable';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { dialogState, selectedPatientState, visitListState } from 'state';
import useHasAccess from 'hooks/useHasAccess';
import { Permission } from 'state/permissionState';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add } from '@mui/icons-material';
import { FormTemplateEditorMode } from 'routes/FormTemplateEditor/FormTemplateEditor';

export default function PatientDetails() {
  const { patientId, projectId } = useParams<GeneralIds>();
  const [visits, setVisits] = useRecoilState(visitListState);
  const [formTemplateTypes, setFormTemplateTypes] = useState<
    FormTemplateType[]
  >([]);
  const [sampleTemplateTypes, setSampleTemplateTypes] = useState<
    FormTemplateType[]
  >([]);

  const [reportTemplateTypes, setReportTemplateTypes] = useState<
    FormTemplateType[]
  >([]);

  const patient = useRecoilValue(selectedPatientState);
  const canViewVisits = useHasAccess([Permission.READ_VISIT]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const canAddVisit = useHasAccess([Permission.WRITE_VISIT]);
  const openDialog = useSetRecoilState(dialogState);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (canViewVisits) {
        const { data: visits } = await query<Visit[]>({
          type: ModelTypes.visit,
          ids: { patientId },
        });
        setVisits(visits);
      }

      const { data: formTemplateTypes } = await query<FormTemplateType[]>({
        type: ModelTypes.formTemplateType,
        ids: { projectId },
      });

      const { data: sampleTemplateTypes } = await query<FormTemplateType[]>({
        type: ModelTypes.formTemplateType,
        queryParams: {
          type: FormTemplateEditorMode.sample,
        },
        ids: { projectId },
      });
      const { data: reportTemplateTypes } = await query<FormTemplateType[]>({
        type: ModelTypes.formTemplateType,
        queryParams: {
          type: FormTemplateEditorMode.report,
        },
        ids: { projectId },
      });

      setSampleTemplateTypes(sampleTemplateTypes);
      setFormTemplateTypes(formTemplateTypes);
      setReportTemplateTypes(reportTemplateTypes);
      setLoading(false);
    };
    fetchData();
  }, [patientId, projectId, setVisits, canViewVisits]);

  return (
    <Box mt={2}>
      <PatientCard patient={patient} />
      {canViewVisits && (
        <Card sx={{ mt: 2 }}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h5" ml={1} mt={2}>
              {t('home.menu.visits')}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              disabled={!canAddVisit}
              sx={{ mt: 2 }}
              onClick={() =>
                openDialog({ isOpen: true, dialogType: DialogTypes.addVisit })
              }
            >
              {t('visits.addVisit')}
            </Button>
          </CardContent>
          {loading ? (
            <Box
              width="100%"
              height="150px"
              display="flex"
              justifyContent={'center'}
              alignItems={'center'}
            >
              <CircularProgress />
            </Box>
          ) : (
            <VisitsTable
              visits={visits}
              formTemplateTypes={formTemplateTypes}
              sampleTemplateTypes={sampleTemplateTypes}
              reportTemplateTypes={reportTemplateTypes}
            />
          )}
        </Card>
      )}
    </Box>
  );
}
